import React, { useEffect, useCallback, useMemo } from "react";
import CardTable from "components/CardTable/CardTable";
import { TableInputs } from "components/CardTable/TableInputs";
import { useOpenComponent } from "hooks/useOpenComponent";

import { useDispatch, useSelector } from "react-redux";

import AddIcon from "@mui/icons-material/Add";
import { columns } from "./columns";

import RegularButton from "components/CustomButtons/Button";
import { Home } from "@mui/icons-material";
import { useSearch } from "hooks/useSearch";
import { ModalV2 } from "components/Modal/ModalV2";
import { MetersForm } from "./MetersForm";
import { METERS_PHRASE } from "./config";
import {
  fetchMeters,
  newMeter,
  clearMeters,
  handleMetersInputsAction,
} from "redux/actions/buildings/meters/metersAction";

const LOADINGS = ["fetchMeters", "fetchNewMeter", "fetchEditMeter"];

export const MetersPage = () => {
  const dispatch = useDispatch();
  const { isOpen, handleIsOpen } = useOpenComponent();

  const {
    setSnackbar,
    clearSearch,
    buildCompLoadings
  } = useSearch();

  const { isLoadingComp } = useSelector(
    state => state.search
  );
  const { metersMap, station, isAddMode, deleteMode } = useSelector(state => state.meters);
  const { models } = useSelector(state => state.brandsMeters);

  const metersMemo = useMemo(
    () =>
      Object.keys(metersMap)
        ?.map(id => metersMap[id])
        .map(meter => {
          const newModel = models[meter.idModel];
          return {
            ...meter,
            model: newModel?.name ?? "",
            brand: newModel?.brand ?? "",
            modal: handleIsOpen
          };
        }),
    [metersMap]
  );

  const getMeters = useCallback(
    stationID => {
      dispatch(fetchMeters(stationID));
    },
    [dispatch, fetchMeters]
  );

  const handleSubmit = useCallback(() => {
    if (station.length < 1) {
      setSnackbar(METERS_PHRASE.search.messages.noStations);
      return;
    }
    const stationID = station.id;

    getMeters(stationID);
  }, [station, setSnackbar]);

  const submitMemoized = useCallback((e) => {
    e.preventDefault();
    handleSubmit();
  }, [handleSubmit]);

  const handleClose = () => {
    handleIsOpen();
  };

  const handleOpenAdd = () => {
    dispatch(newMeter());
    handleIsOpen();
  };

  useEffect(() => {
    clearSearch();
    dispatch(clearMeters());
  }, [clearSearch]);

  useEffect(() => {
    buildCompLoadings(LOADINGS);
  }, []);

  return (
    <React.Fragment>
      <CardTable
        data={metersMemo}
        columns={columns}
        loading={isLoadingComp.fetchMeters}
        title={METERS_PHRASE.page.table.title}
        icon={<Home />}
        hasHeader
        blockBtn
        hasControl
        controls={
          true && (
            <RegularButton
              round
              justIcon
              color="info"
              style={{ margin: 10 }}
              onClick={handleOpenAdd}
              size="sm"
            >
              <AddIcon style={{ margin: 0 }} />
            </RegularButton>
          )
        }
        tableContent={
          <TableInputs
            reducer="meters"
            stations
            dispatchAction={handleMetersInputsAction}
            submitAction={submitMemoized}
          />
        }
      />
      <ModalV2
        open={isOpen}
        onClose={handleClose}
        toRight
        title={isAddMode
          ? METERS_PHRASE.modal.titles.add
          : deleteMode
            ? METERS_PHRASE.modal.titles.delete
            : METERS_PHRASE.modal.titles.edit}
      >
        {
          <MetersForm
            handleClose={handleClose}
            stationsSelected={station}
          />
        }
      </ModalV2>
    </React.Fragment>
  );
};
